import { ArrowDownwardRounded, ArrowUpwardRounded, FilterList } from "@mui/icons-material";
import { Badge, Button, Menu, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import {
  ActionItemStatus,
  SortByDirection,
  UserStrategyFilterByOption,
  UserStrategyGroupByOption,
  UserStrategySortByOption,
} from "../../../constants";
import { UserStrategyFilterToggleButton } from "./components";
import { useUserStrategySettings } from "./hooks";

interface IProps {
  userGuid: string;
  disabled?: boolean;
}

const UserStrategyFilterButton = (props: IProps) => {
  const { settings, updateSettings } = useUserStrategySettings(props.userGuid);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  function handleFilterByChanged(
    filterByOption: UserStrategyFilterByOption,
    filterByStatusOptions: ActionItemStatus[] | null,
  ) {
    updateSettings({
      filterByOption: filterByOption,
      filterByStatusOptions: filterByStatusOptions,
    });
  }

  function handleSortDirectionChanged() {
    updateSettings({
      sortByDirection:
        settings.sortByDirection === SortByDirection.Descending
          ? SortByDirection.Ascending
          : SortByDirection.Descending,
    });
  }

  return (
    <>
      <Badge color="primary" variant="dot" invisible={settings.filterByOption === UserStrategyFilterByOption.All}>
        <Button
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          variant="tertiary"
          disabled={props.disabled}
          sx={{ minWidth: 0, p: 0.25 }}
        >
          <FilterList />
        </Button>
      </Badge>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: "200px" } } }}
      >
        <Typography variant="overline" sx={{ mx: 0.5, mt: 0.5 }}>
          Group By
        </Typography>
        <ToggleButtonGroup
          value={settings.groupByOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_e, value) => {
            updateSettings({
              groupByOption: value,
            });
          }}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value={UserStrategyGroupByOption.None}>
            <Typography>None</Typography>
          </ToggleButton>
          <ToggleButton value={UserStrategyGroupByOption.Status}>
            <Typography>Status</Typography>
          </ToggleButton>
          <ToggleButton value={UserStrategyGroupByOption.Team}>
            <Typography>Team</Typography>
          </ToggleButton>
          <ToggleButton value={UserStrategyGroupByOption.DueDate}>
            <Typography>Due Date</Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="overline" sx={{ mx: 0.5, mt: 1 }}>
          Filter By
        </Typography>
        <UserStrategyFilterToggleButton
          filterByOption={settings.filterByOption}
          filterByStatusOptions={settings.filterByStatusOptions}
          onChange={handleFilterByChanged}
        />

        <Typography variant="overline" sx={{ mx: 0.5, mt: 1 }}>
          Sort By
        </Typography>
        <ToggleButtonGroup
          value={settings.sortByOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_e, value) => {
            if (value !== null) {
              updateSettings({
                sortByOption: value,
              });
            }
          }}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton
            value={UserStrategySortByOption.DateCreated}
            onClick={handleSortDirectionChanged}
            sx={{ justifyContent: "space-between !important" }}
          >
            <Typography>Date Created</Typography>
            {settings.sortByOption === UserStrategySortByOption.DateCreated ? (
              settings.sortByDirection === SortByDirection.Descending ? (
                <ArrowDownwardRounded />
              ) : (
                <ArrowUpwardRounded />
              )
            ) : null}
          </ToggleButton>
          <ToggleButton
            value={UserStrategySortByOption.DateModified}
            onClick={handleSortDirectionChanged}
            sx={{ justifyContent: "space-between !important" }}
          >
            <Typography>Date Modified</Typography>
            {settings.sortByOption === UserStrategySortByOption.DateModified ? (
              settings.sortByDirection === SortByDirection.Descending ? (
                <ArrowDownwardRounded />
              ) : (
                <ArrowUpwardRounded />
              )
            ) : null}
          </ToggleButton>
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

export { UserStrategyFilterButton };
